/**
 * Date / time formatting helpers.
 *
 * Phase 4: formatted output uses the browser's local timezone via
 * `Intl.DateTimeFormat`. Moderators in any timezone see dates in their
 * own clock. The `localTimezoneLabel()` helper exposes the resolved
 * timezone (e.g. "Australia/Brisbane") so UI can hint at it next to
 * time-heavy displays.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * Resolve the local IANA timezone name (best effort). Returns
 * an empty string when the environment doesn't support it.
 */
export function localTimezoneLabel(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || '';
  } catch {
    return '';
  }
}

/**
 * Format a date as "D Mon YYYY" in the user's local timezone.
 *
 * Uses local getDate / getMonth / getFullYear so the display matches the
 * moderator's wall clock. Tests can pin a specific timezone via the TZ env
 * variable so output is deterministic.
 */
export function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Format a date as "D Mon YYYY HH:MM" in the user's local timezone. */
export function formatDateTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = formatDate(iso);
  return `${date} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function relativeTime(iso: string, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const absSec = Math.abs(diffSec);

  if (absSec < 45) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  const absMin = Math.abs(diffMin);
  if (absMin < 60) {
    return diffMin >= 0
      ? `${absMin} minute${absMin === 1 ? '' : 's'} ago`
      : `in ${absMin} minute${absMin === 1 ? '' : 's'}`;
  }
  const diffHr = Math.round(diffMin / 60);
  const absHr = Math.abs(diffHr);
  if (absHr < 24) {
    return diffHr >= 0
      ? `${absHr} hour${absHr === 1 ? '' : 's'} ago`
      : `in ${absHr} hour${absHr === 1 ? '' : 's'}`;
  }
  const diffDay = Math.round(diffHr / 24);
  const absDay = Math.abs(diffDay);
  if (absDay < 30) {
    return diffDay >= 0
      ? `${absDay} day${absDay === 1 ? '' : 's'} ago`
      : `in ${absDay} day${absDay === 1 ? '' : 's'}`;
  }
  const diffMonth = Math.round(diffDay / 30);
  const absMonth = Math.abs(diffMonth);
  if (absMonth < 12) {
    return diffMonth >= 0
      ? `${absMonth} month${absMonth === 1 ? '' : 's'} ago`
      : `in ${absMonth} month${absMonth === 1 ? '' : 's'}`;
  }
  const diffYear = Math.round(diffDay / 365);
  const absYear = Math.abs(diffYear);
  return diffYear >= 0
    ? `${absYear} year${absYear === 1 ? '' : 's'} ago`
    : `in ${absYear} year${absYear === 1 ? '' : 's'}`;
}

export function truncate(text: string, maxChars: number): string {
  if (!text) return '';
  if (text.length <= maxChars) return text;
  if (maxChars <= 1) return text.slice(0, maxChars);
  return `${text.slice(0, Math.max(0, maxChars - 1))}…`;
}
